import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const inviteRequestBodySchema = z.object({
  workspace_id: z.string().uuid(),
  invited_email: z.string().email().optional(),
});

export const inviteSuccessSchema = z.object({
  invite_url: z.string().min(1),
});

export const joinRequestBodySchema = z.object({
  invite_code: z.string().min(1).max(128),
  user_id: z.string().min(1).max(128),
});

export const joinSuccessSchema = z.object({
  workspace_id: z.string().uuid(),
  joined: z.boolean().optional(),
  already_member: z.boolean().optional(),
});

export type InviteRequestBody = z.infer<typeof inviteRequestBodySchema>;
export type InviteSuccess = z.infer<typeof inviteSuccessSchema>;
export type JoinRequestBody = z.infer<typeof joinRequestBodySchema>;
export type JoinSuccess = z.infer<typeof joinSuccessSchema>;

export function invitePath(): string {
  return "/api/invite";
}

export function joinPath(): string {
  return "/api/join";
}

function buildAuthHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function createInviteRequest({
  apiBaseUrl,
  workspaceId,
  invitedEmail,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  workspaceId: string;
  invitedEmail?: string;
  accessToken?: string;
  fetchImpl?: FetchLike;
}): Promise<InviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = inviteRequestBodySchema.parse({
    workspace_id: workspaceId,
    invited_email: invitedEmail,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${invitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create invite");
  }

  const payload = await response.json().catch(() => null);
  const parsed = inviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function joinWorkspaceByInviteRequest({
  apiBaseUrl,
  inviteCode,
  userId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  inviteCode: string;
  userId: string;
  accessToken?: string;
  fetchImpl?: FetchLike;
}): Promise<JoinSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = joinRequestBodySchema.parse({
    invite_code: inviteCode,
    user_id: userId,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${joinPath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to join workspace");
  }

  const payload = await response.json().catch(() => null);
  const parsed = joinSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid join response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
